import type { Location, NodeEntry } from 'slate';
import { Editor, Path, Transforms } from 'slate';

import type {
  AlignmentType,
  AlineText,
  FooterElement,
  HeaderElement,
  InlineInstructionFieldElement,
  ParagraphElement,
  SuggestedEdit,
  SuggestedInstructionFieldEdit,
} from '../internal';
import {
  AlineTransforms,
  createDefaultParagraph,
  createDefaultText,
  EditingMode,
  FooterType,
  HeaderType,
  instructionFieldElementType,
  isHeaderElement,
  isInlineInstructionFieldElement,
  isParagraphElement,
  Queries,
} from '../internal';

interface PageNumberingOptions {
  alignment?: AlignmentType;
  format?: 'none' | 'lowerLetter' | 'lowerRoman' | 'numberInDash' | 'upperLetter' | 'upperRoman';
  includePageCount?: boolean;
}

interface InsertIntoHeaderOrFooterOptions {
  alignment: AlignmentType;
  authorId: string;
  editingMode: EditingMode;
  headerOrFooterEntry: NodeEntry<HeaderElement | FooterElement>;
  includePageCount?: boolean;
}

interface RemovePageNumberingOptions {
  authorId: string;
  editingMode: EditingMode;
}

interface InsertPageNumberingOptions {
  alignment: AlignmentType;
  authorId: string;
  editingMode: EditingMode;
  includePageCount: boolean;
  location: 'header' | 'footer';
  pageNumberingOptions?: PageNumberingOptions;
}

interface InsertionOptions {
  canInsertIntoChild: boolean;
  insertAt: Path;
}

/**
 * Creates an instruction field element for page numbering
 */
function createPageNumberingElement(
  authorId: string,
  editingMode: EditingMode,
  instruction: 'PAGE' | 'NUMPAGES'
): InlineInstructionFieldElement {
  const insertEdit: SuggestedInstructionFieldEdit = {
    authorId,
    instruction,
    type: 'insertInstructionField',
    value: {},
  };

  return {
    children: [
      {
        ...createDefaultText({}, '10000'),
      },
    ],
    instruction,
    suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertEdit] : [],
    switches: '',
    type: instructionFieldElementType,
  };
}

/**
 * Creates a separator text element for page numbering
 */
function createSeparatorText(authorId: string, editingMode: EditingMode): AlineText {
  const insertEdit: SuggestedEdit = {
    authorId,
    type: 'insertNode',
    value: {},
  };

  return {
    ...createDefaultText(),
    suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertEdit] : [],
    text: ' of ',
  };
}

/**
 * Creates the nodes needed for page numbering
 */
function createPageNumberingNodes(
  authorId: string,
  editingMode: EditingMode,
  includePageCount: boolean
): (InlineInstructionFieldElement | AlineText)[] {
  const pageNumberField = createPageNumberingElement(authorId, editingMode, 'PAGE');

  const nodesToBeInserted: (InlineInstructionFieldElement | AlineText)[] = [pageNumberField];

  if (includePageCount) {
    const separatorText = createSeparatorText(authorId, editingMode);

    const totalPagesField = createPageNumberingElement(authorId, editingMode, 'NUMPAGES');

    nodesToBeInserted.push(separatorText, totalPagesField);
  }

  return nodesToBeInserted;
}

function getHeaderEntry(editor: Editor, headerType: HeaderType): NodeEntry<HeaderElement> {
  const headersEntry = Queries.getHeadersElement(editor);
  if (!headersEntry) throw new Error('Expected headers.');

  return Queries.getHeaderOrFooter<'header'>({ editor, partEntry: headersEntry, type: headerType });
}

function getFooterEntry(editor: Editor, footerType: FooterType): NodeEntry<FooterElement> {
  const footersEntry = Queries.getFootersElement(editor);
  if (!footersEntry) throw new Error('Expected footers.');

  return Queries.getHeaderOrFooter<'footer'>({ editor, partEntry: footersEntry, type: footerType });
}

function getInsertionOptions(
  editor: Editor,
  headerOrFooterEntry: NodeEntry<HeaderElement | FooterElement>
): InsertionOptions {
  let child;
  let childPath: Path;

  const [entryNode, entryPath] = headerOrFooterEntry;

  const isHeader = isHeaderElement(entryNode);

  if (isHeader) {
    [child, childPath] = Editor.node(editor, entryPath.concat(0));
  } else {
    [child, childPath] = Editor.node(editor, entryPath.concat(entryNode.children.length - 1));
  }

  const isChildParagraph = isParagraphElement(child);

  if (!isChildParagraph) {
    return {
      canInsertIntoChild: false,
      insertAt: isHeader ? childPath : Path.next(childPath),
    };
  }

  const isChildEmpty = Queries.nodeIsEmpty(child);

  return {
    canInsertIntoChild: isChildEmpty,
    insertAt: isHeader ? childPath : isChildEmpty ? childPath : Path.next(childPath),
  };
}

/**
 * Inserts page numbering nodes into an existing or new paragraph
 */
function insertPageNumberingNodes(
  editor: Editor,
  at: Location,
  nodesToBeInserted: (InlineInstructionFieldElement | AlineText)[],
  canInsertIntoExistingParagraph: boolean
) {
  if (canInsertIntoExistingParagraph) {
    Transforms.insertNodes(editor, nodesToBeInserted, {
      at: Editor.range(editor, at),
    });
  } else {
    const newParagraph: ParagraphElement = {
      ...createDefaultParagraph(),
      children: nodesToBeInserted,
    };

    Transforms.insertNodes(editor, newParagraph, {
      at,
    });
  }
}

/**
 * Inserts page numbering nodes into a header
 *
 *  - Removes any existing page numbering nodes from the header or footer.
 *  - If the first child of the header is a paragraph and is empty, the page numbering nodes are inserted into it.
 *  - If the first child of the header is not empty, the page numbering nodes are inserted inside new paragraph at the beginning of the header.
 *  - If the last child of the footer is a paragraph and is empty, the page numbering nodes are inserted into it.
 *  - If the last child of the footer is not empty, the page numbering nodes are inserted inside new paragraph at the end of the footer.
 */
function insertIntoHeaderOrFooter(editor: Editor, options: InsertIntoHeaderOrFooterOptions) {
  const { alignment, authorId, editingMode, headerOrFooterEntry, includePageCount } = options;

  const { canInsertIntoChild, insertAt } = getInsertionOptions(editor, headerOrFooterEntry);

  const nodesToBeInserted = createPageNumberingNodes(
    authorId,
    editingMode,
    includePageCount || false
  );

  insertPageNumberingNodes(editor, insertAt, nodesToBeInserted, canInsertIntoChild);

  if (alignment) {
    AlineTransforms.setParagraphProps(
      editor,
      editingMode,
      authorId,
      { alignment },
      { at: insertAt }
    );
  }
}

/**
 * Removes page numbering fields at a specific path
 */
function removePageNumberingFieldsAt(
  editor: Editor,
  targetPath: Location,
  authorId: string,
  editingMode: EditingMode
): void {
  const instructionFieldEntries = Editor.nodes(editor, {
    at: targetPath,
    match: n => isInlineInstructionFieldElement(n) && n.instruction === 'PAGE',
    reverse: true,
  });

  for (const [, instructionPath] of instructionFieldEntries) {
    const parentPath = instructionPath.slice(0, -1);

    const numpagesNodeEntries = Editor.nodes(editor, {
      at: parentPath,
      match: (n, p) =>
        isInlineInstructionFieldElement(n) &&
        n.instruction === 'NUMPAGES' &&
        Path.isAfter(p, instructionPath),
    });

    const firstNumpagesEntry = numpagesNodeEntries.next().value;

    if (firstNumpagesEntry) {
      const [, numPagesPath] = firstNumpagesEntry;

      const rangeToRemove = Editor.range(editor, {
        anchor: Editor.start(editor, instructionPath),
        focus: Editor.end(editor, numPagesPath),
      });

      if (editingMode === EditingMode.SUGGESTING) {
        AlineTransforms.suggestDeletion(editor, {
          at: rangeToRemove,
          authorId,
          editingMode,
          reverse: true,
        });
        continue;
      }

      Transforms.delete(editor, { at: rangeToRemove });
    } else {
      if (editingMode === EditingMode.SUGGESTING) {
        AlineTransforms.suggestDeletion(editor, {
          at: instructionPath,
          authorId,
          editingMode,
        });
        continue;
      }

      Transforms.removeNodes(editor, { at: instructionPath });
    }
  }
}

/**
 * Removes page numbering from a header by type
 */
function removePageNumberingFromHeader(
  editor: Editor,
  headerType: HeaderType,
  authorId: string,
  editingMode: EditingMode
): void {
  const headerEntry = getHeaderEntry(editor, headerType);

  if (headerEntry) {
    const [, headerPath] = headerEntry;
    removePageNumberingFieldsAt(editor, headerPath, authorId, editingMode);
  }
}

/**
 * Removes page numbering from a footer by type
 */
function removePageNumberingFromFooter(
  editor: Editor,
  footerType: FooterType,
  authorId: string,
  editingMode: EditingMode
): void {
  const footerEntry = getFooterEntry(editor, footerType);

  if (footerEntry) {
    const [, footerPath] = footerEntry;
    removePageNumberingFieldsAt(editor, footerPath, authorId, editingMode);
  }
}

/**
 * Main page numbering transforms object
 */
const PageNumberingTransforms = {
  insertPageNumbering(editor: Editor, options: InsertPageNumberingOptions) {
    const { alignment, authorId, editingMode, includePageCount, location } = options;

    const [document] = Queries.getDocument(editor);
    const { diffFirstPage, evenOdd } = Queries.activeHeaderOrFooterProps(document);

    // Helper to insert into header or footer by type
    const insertAtType = (type: HeaderType | FooterType) => {
      Editor.withoutNormalizing(editor, () => {
        if (location === 'header') {
          removePageNumberingFromHeader(editor, type as HeaderType, authorId, editingMode);
          const headerEntry = getHeaderEntry(editor, type as HeaderType);
          insertIntoHeaderOrFooter(editor, {
            alignment,
            authorId,
            editingMode,
            headerOrFooterEntry: headerEntry,
            includePageCount,
          });
        } else {
          removePageNumberingFromFooter(editor, type as FooterType, authorId, editingMode);
          const footerEntry = getFooterEntry(editor, type as FooterType);
          insertIntoHeaderOrFooter(editor, {
            alignment,
            authorId,
            editingMode,
            headerOrFooterEntry: footerEntry,
            includePageCount,
          });
        }
      });
    };

    if (location === 'header') {
      // Handle first page header
      if (
        diffFirstPage &&
        (Queries.isSelectionInPageHeader(editor, HeaderType.FirstPage) ||
          Queries.isSelectionOnFirstPageBody(editor))
      ) {
        insertAtType(HeaderType.FirstPage);
        return;
      }

      // Handle even page headers
      if (
        evenOdd &&
        (Queries.isSelectionInPageHeader(editor, HeaderType.Even) ||
          Queries.isSelectionOnEvenPageBody(editor))
      ) {
        insertAtType(HeaderType.Even);
        return;
      }

      // Handle odd page headers and default case
      insertAtType(HeaderType.Default);
    } else {
      // Handle first page footer
      if (
        diffFirstPage &&
        (Queries.isSelectionInPageFooter(editor, FooterType.FirstPage) ||
          Queries.isSelectionOnFirstPageBody(editor))
      ) {
        insertAtType(FooterType.FirstPage);
        return;
      }

      // Handle even page footers
      if (
        evenOdd &&
        (Queries.isSelectionInPageFooter(editor, FooterType.Even) ||
          Queries.isSelectionOnEvenPageBody(editor))
      ) {
        insertAtType(FooterType.Even);
        return;
      }

      // Handle odd page footers and default case
      insertAtType(FooterType.Default);
    }
  },

  /**
   * Removes page numbering from the document based on current context
   */
  removePageNumbering(editor: Editor, options: RemovePageNumberingOptions): void {
    const { authorId, editingMode } = options;

    const headerTypeAboveSelection = Queries.getHeaderTypeAboveSelection(editor);
    const footerTypeAboveSelection = Queries.getFooterTypeAboveSelection(editor);

    if (headerTypeAboveSelection) {
      removePageNumberingFromHeader(editor, headerTypeAboveSelection, authorId, editingMode);
      return;
    }

    if (footerTypeAboveSelection) {
      removePageNumberingFromFooter(editor, footerTypeAboveSelection, authorId, editingMode);
      return;
    }

    const [document] = Queries.getDocument(editor);
    const { diffFirstPage, evenOdd } = Queries.activeHeaderOrFooterProps(document);

    if (Queries.isSelectionOnFirstPageBody(editor) && diffFirstPage) {
      removePageNumberingFromHeader(editor, HeaderType.FirstPage, authorId, editingMode);
      removePageNumberingFromFooter(editor, FooterType.FirstPage, authorId, editingMode);
      return;
    }

    if (Queries.isSelectionOnEvenPageBody(editor) && evenOdd) {
      removePageNumberingFromHeader(editor, HeaderType.Even, authorId, editingMode);
      removePageNumberingFromFooter(editor, FooterType.Even, authorId, editingMode);
      return;
    }

    removePageNumberingFromHeader(editor, HeaderType.Default, authorId, editingMode);
    removePageNumberingFromFooter(editor, FooterType.Default, authorId, editingMode);
  },
};

export default PageNumberingTransforms;
